import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CaretRight, MagnifyingGlass } from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Input } from 'src/components/Input';
import { getInsightTypeLabel } from 'src/components/insights/constants';
import { Modal } from 'src/components/Modal';
import { useNotification } from 'src/contexts/NotificationContext';
import { Insight } from 'src/interfaces/agent.interface';
import { useInsights } from 'src/pages/Agents/contexts/InsightsContext';
import { cn } from 'src/utils/className';

type SelectExistingInsightsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (insights: Insight[]) => void;
  excludeIds: string[];
};

export const SelectExistingInsightsModal: React.FC<
  SelectExistingInsightsModalProps
> = ({ isOpen, onClose, onAdd, excludeIds }) => {
  const { listInsights } = useInsights();
  const notification = useNotification();

  const [allInsights, setAllInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, { wait: 300 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Fetch insights when modal opens or debounced query changes
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const items = await listInsights(debouncedQuery || undefined);
        if (!cancelled) {
          setAllInsights(items);
        }
      } catch {
        if (!cancelled) {
          notification.error('Failed to load insights');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedQuery, listInsights, notification]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIds(new Set());
      setExpandedIds(new Set());
    }
  }, [isOpen]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const filteredInsights = useMemo(
    () => allInsights.filter((i) => !excludeSet.has(i.id)),
    [allInsights, excludeSet],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleAdd = useCallback(() => {
    const selectedInsights = allInsights.filter((i) => selectedIds.has(i.id));
    onAdd(selectedInsights);
  }, [allInsights, selectedIds, onAdd]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Existing Insights"
      subtitle="Select one or more insights to add to this agent."
      showClose
      showModalBorder
      className="lg:max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-4 pt-4 px-6 pb-6 border-t border-neutral-200">
          <div className="flex items-center gap-3 text-sm text-neutral-500">
            <span>{selectedIds.size} selected</span>
            {selectedIds.size > 0 && (
              <Button
                variant="text"
                color="primary"
                size="small"
                className="px-0 py-0 text-sm hover:bg-transparent active:bg-transparent hover:underline"
                onClick={clearSelection}
              >
                Clear selection
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outlined" onClick={onClose} size="small">
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={selectedIds.size === 0}
              size="small"
            >
              Add selected
            </Button>
          </div>
        </div>
      }
    >
      <div className="py-4 space-y-3">
        <Input
          placeholder="Search insights by description or ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          icon={<MagnifyingGlass className="w-4 h-4" />}
          iconPosition="left"
        />

        <div className="rounded-lg border border-neutral-200 bg-neutral-0 overflow-hidden">
          <div className="max-h-80 overflow-y-auto">
            {loading && filteredInsights.length === 0 ? (
              <div className="p-6 text-sm text-neutral-400 text-center">
                Loading insights...
              </div>
            ) : filteredInsights.length === 0 ? (
              <div className="p-6 text-sm text-neutral-400 text-center">
                {query
                  ? 'No matching insights.'
                  : 'No additional insights available to add.'}
              </div>
            ) : (
              filteredInsights.map((insight) => {
                const checked = selectedIds.has(insight.id);
                const expanded = expandedIds.has(insight.id);

                return (
                  <div
                    key={insight.id}
                    className={cn(
                      'border-b border-neutral-200 last:border-b-0',
                      checked && 'bg-primary-100',
                    )}
                  >
                    <div className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
                      <div className="mt-0.5 flex-shrink-0">
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelected(insight.id)}
                          ariaLabel={`Select ${insight.label || insight.id}`}
                        />
                      </div>

                      <Button
                        type="button"
                        variant="text"
                        color="default"
                        size="small"
                        className="h-auto min-w-0 flex-1 justify-start rounded-none px-0 py-0 text-left hover:bg-transparent active:bg-transparent"
                        onClick={() => toggleSelected(insight.id)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3">
                            <span className="truncate font-medium text-sm text-neutral-700">
                              {insight.label || insight.id}
                            </span>
                            <span className="truncate text-xs text-neutral-400">
                              {insight.id}
                            </span>
                            <span className="flex-shrink-0 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs text-neutral-600">
                              {getInsightTypeLabel(insight.type)}
                            </span>
                          </div>
                          <div
                            className={cn(
                              'mt-1 text-xs text-neutral-400',
                              !expanded && 'line-clamp-2',
                            )}
                          >
                            {insight.instructions}
                          </div>
                        </div>
                      </Button>

                      <Button
                        type="button"
                        variant="text"
                        color="default"
                        size="small"
                        className="mt-0.5 flex-shrink-0 gap-1 px-2 py-1 text-xs text-neutral-400"
                        onClick={() => toggleExpanded(insight.id)}
                      >
                        {expanded ? 'Less' : 'More'}
                        <CaretRight
                          className={cn(
                            'h-3 w-3 transition-transform',
                            expanded && 'rotate-90',
                          )}
                          weight="bold"
                        />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
